import { IonButton, IonButtons, IonIcon, IonSpinner, IonToast } from '@ionic/react';
import { arrowBack } from 'ionicons/icons';
import QRCode from 'qrcode';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { MessageContext } from '../../../../../../contexts/MessageContext';
import { apiMercadoPagoOperationCriarPagamento, requestService } from '../../../../../../Utils/Services';

interface Props {
    valorTotal: string;
    pedidoPk: string;
    dadosEmpresa: any;
    onConfirmar: (formaPagamento: any) => void;
    onBack: () => void;
}

const TelaPagamento: React.FC<Props> = ({ valorTotal, pedidoPk, dadosEmpresa, onConfirmar, onBack }) => {
    const history = useHistory();
    const { setCorBase, setShowPagamentoMaquininha, setQrCodeDataMercadoPago, qrCodeDataMercadoPago, setIdPagamentoMaquininha } = useContext(MessageContext);
    const { idPaymentMaquininhaSuccess, setIdPaymentMaquininhaSuccess } = useContext(MessageContext);

    const [formaSelecionada, setFormaSelecionada] = useState<any>(null);
    const [processando, setProcessando] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [textToast, setTextToast] = useState('');

    // Ref para acessar o pagamento em andamento dentro do callback assíncrono
    const pagamentoRef = useRef<any>(null);

    const isMercadoPago = (tipo: string) =>
        (tipo || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes('mercado pago');

    const formasPagamentoDisponiveis = (dadosEmpresa?.formaPagamento || []).filter((item: any) => item.exibirPdv && isMercadoPago(item.tipo)); // Filtra apenas formas de pagamento Mercado Pago;

    // Callback de sucesso do Mercado Pago (maquininha/pix)
    useEffect(() => {
        if (!idPaymentMaquininhaSuccess) return;
        if (pagamentoRef.current?.orderId === idPaymentMaquininhaSuccess) {
            setIdPaymentMaquininhaSuccess('');
            onConfirmar({ ...pagamentoRef.current, pagou: true });
            pagamentoRef.current = null;
        }
    }, [idPaymentMaquininhaSuccess]);

    async function handleConfirmar() {
        if (!formaSelecionada) return;

        const uuid = crypto.randomUUID();
        const pagamento = {
            id: uuid,
            pagamento: formaSelecionada.nome,
            tipo: formaSelecionada.tipo,
            valor: valorTotal,
            pagou: false,
        };

        // Pagamento não-MP: confirma direto
        if (!isMercadoPago(formaSelecionada.tipo)) {
            onConfirmar({ ...pagamento, pagou: true });
            return;
        }

        // Mercado Pago: chama API
        pagamentoRef.current = pagamento;

        const params: any = { amount: valorTotal, orderId: uuid };
        const tipo = formaSelecionada.tipo;

        if (tipo === 'Mercado Pago Pix') {
            params.type = 'qr';
        } else if (tipo === 'Mercado Pago Crédito') {
            params.type = 'point';
            params.paymentType = 'credit';
        } else if (tipo === 'Mercado Pago Débito') {
            params.type = 'point';
            params.paymentType = 'debit';
        } else {
            setTextToast('Tipo de pagamento não suportado.');
            setShowToast(true);
            return;
        }

        params.terminalId = window.localStorage.getItem('terminalIdMercadoPago') || '';
        params.externalPosId = window.localStorage.getItem('externalPosIdMercadoPago') || '';

        setProcessando(true);
        setQrCodeDataMercadoPago('');

        requestService(
            apiMercadoPagoOperationCriarPagamento.url
                .replace('{id}', pedidoPk.replace(/#/g, '%23'))
                .replace('{idEmpresa}', dadosEmpresa.pk.replace(/#/g, '%23')),
            {
                method: apiMercadoPagoOperationCriarPagamento.method,
                headers: [['token', window.localStorage.getItem('token')]],
                body: JSON.stringify(params),
                credentials: 'include',
            },
            history,
            async (response: any) => {
                setProcessando(false);

                if (response.statusCode === 201 && response.data?.id) {
                    setIdPagamentoMaquininha(response.data.id);
                    setCorBase("danger");
                    setShowPagamentoMaquininha(true);
                    // Mantém orderId (uuid) para o callback de sucesso; id passa a ser o id do MP
                    pagamentoRef.current = { ...pagamento, orderId: pagamento.id, id: response.data.id };

                    if (tipo === 'Mercado Pago Pix' && response.data.type_response?.qr_data) {
                        const qrData = await QRCode.toDataURL(response.data.type_response.qr_data);
                        setQrCodeDataMercadoPago(qrData);
                        setTextToast('Efetue o pagamento via QrCode.');
                    } else {
                        setTextToast('Efetue o pagamento na maquininha.');
                    }
                    setShowToast(true);
                } else {
                    pagamentoRef.current = null;
                    setTextToast('Ocorreu um erro ao processar, tente novamente.');
                    setShowToast(true);
                }
            },
            (error: any) => {
                setProcessando(false);
                pagamentoRef.current = null;
                setTextToast(
                    error.status === 409
                        ? 'Aguarde, pagamento em processamento.'
                        : 'Ocorreu um erro ao processar, tente novamente.'
                );
                setShowToast(true);
            }
        );
    }

    return (
        <div className="totem-tela-pagamento">
            <div className="totem-tela-header">
                <IonButtons>
                    <IonButton fill="clear" color="medium" onClick={onBack} className="totem-btn-voltar">
                        <IonIcon icon={arrowBack} slot="icon-only" />
                    </IonButton>
                </IonButtons>

            </div>

            <div className="totem-tela-body">
                <h2>Pagamento</h2>

                <div className="totem-pagamento-valor">
                    <h3>Valor total:</h3>
                    <h1>R$ {valorTotal}</h1>
                </div>

                {processando ? (
                    <div className="totem-pagamento-processando">
                        <IonSpinner name="crescent" color="danger" />
                        <h3>Processando pagamento...</h3>
                        <p>
                            {formaSelecionada?.tipo === 'Mercado Pago Pix'
                                ? 'Aguardando confirmação do Pix'
                                : 'Efetue o pagamento na maquininha'}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* <p style={{ textAlign: 'center', color: '#666', marginBottom: '16px', fontSize: '1rem' }}>
                            Selecione a forma de pagamento:
                        </p> */}
                        <div className="totem-pagamento-valor">
                            <h3>Como deseja pagar?</h3>
                        </div>

                        <div className="totem-pagamento-opcoes">


                            {formasPagamentoDisponiveis.map((item: any, index: number) => (
                                <div
                                    key={index}
                                    className={`totem-pagamento-card${formaSelecionada?.tipo === item.tipo ? ' totem-pagamento-card-ativo' : ''}`}
                                    onClick={() => setFormaSelecionada(item)}
                                >
                                    <h3>{item.nome}</h3>
                                    {item.descricao && <p>{item.descricao}</p>}
                                </div>
                            ))}
                        </div>

                        {/* QR Code (Pix aguardando) */}
                        {qrCodeDataMercadoPago && (
                            <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                <img src={qrCodeDataMercadoPago} alt="QR Code Pix" style={{ maxWidth: '220px' }} />
                                <p style={{ color: '#666', marginTop: '8px' }}>Escaneie o QR Code para pagar via Pix</p>
                            </div>
                        )}


                    </>
                )}
            </div>

            <div className="totem-tela-actions">
                <IonButton
                    className="totem-btn"
                    expand="block"
                    color="danger"
                    disabled={!formaSelecionada}
                    onClick={handleConfirmar}
                >
                    Confirmar Pagamento
                </IonButton>
            </div>

            <IonToast
                isOpen={showToast}
                onDidDismiss={() => setShowToast(false)}
                message={textToast}
                duration={3000}
                position="middle"
            />
        </div>
    );
};

export default TelaPagamento;

